/**
 * Picker that configures which onboarding agent serves a given
 * project. Backs Issue #5 by closing the
 * "No agent configured for project <id>" gap from the portal UI —
 * before this, a new project (e.g. samuel-sandbox) had no `project_agent`
 * row until someone hand-ran `set_project_agent` over /call, and every
 * Orientation Assignment (then "Cohort Builder") Assign on it rejected.
 *
 * Renders as the "Project onboarding agent" row in Settings' Site card.
 * Dropdown options come from mp `presence_state` filtered to
 * `actor_type === Agent` (the existing subscription, no new
 * connection). The current selection persists `display_name` from the
 * `project_agent` row itself, so a currently-offline agent still
 * renders with its name rather than blanking the picker — matches the
 * recurring minezero-sandbox case.
 *
 * #219 — one agent, one project. Agents registered for another site, or
 * already serving another project, are listed but disabled with the
 * reason (rules in lib/agentBinding); a current binding that is already
 * wrong gets a warning; and every visible site's agent is listed below so
 * a wrong binding elsewhere is not invisible from here. The module refuses
 * the "already serves another project" case on its own.
 */

import { useMemo } from 'react';
import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from '@/components/Icon';
import { useTable } from 'spacetimedb/react';
import { tables as minetwinTables } from '@sdk/minetwin';
import { useAvailableAgents } from '@/hooks/useAvailableAgents';
import { useProjectAgents } from '@/hooks/useProjectAgents';
import { useRegisteredAgents } from '@/hooks/useRegisteredAgents';
import { useRunReducer } from '@/hooks/useRunReducer';
import {
  agentConflict,
  conflictLabel,
  currentBindingWarning,
  siteAgentText,
  type AgentConflict,
  type BindingRef,
} from '@/lib/agentBinding';
import { hasSiteMembership } from '@/lib/siteMembership';
import { AgentBindingsList, type AgentBindingLine } from './AgentBindingsList';
import type { Site, Toast } from '@/data/types';

type ProjectAgentPickerProps = {
  site: Site;
  /** Every site the admin can see — names the other sites in warnings. */
  sites: Site[];
  toast: (t: Omit<Toast, 'id'>) => void;
};

export function ProjectAgentPicker({ site, sites, toast }: ProjectAgentPickerProps) {
  const projectId = site.projectName;
  const siteName = site.name;
  const { agents: available, isReady: availableReady } = useAvailableAgents();
  const { agents: bindingRows, byProjectId, isReady: projectAgentsReady } = useProjectAgents();
  const registered = useRegisteredAgents();
  // The viewer's own site memberships: where a missing binding really means
  // "no agent" rather than "out of your scope" (#219 review).
  const [mySites] = useTable('minetwin', minetwinTables.my_user_sites);
  const { runReducer, busy } = useRunReducer(toast);

  const current = byProjectId(projectId);
  const bindings = useMemo<BindingRef[]>(
    () =>
      bindingRows.map((r) => ({
        projectId: r.projectId,
        identityHex: r.agentIdentity.toHexString(),
        displayName: r.displayName,
      })),
    [bindingRows],
  );

  // Build dropdown options. The <select> element value is always a
  // string, so we key options by `identityHex` (the option's `value`)
  // and resolve the picked option back to its Identity on change.
  // Identity itself is opaque on the React side.
  //
  // If the configured agent isn't in the live agent list (e.g. service
  // offline), keep its row pinned at the top as an "(offline)" entry
  // so the picker still shows what's saved.
  const currentHex = current?.agentIdentity.toHexString() ?? '';
  type Option = {
    value: string;
    label: string;
    isConnected: boolean;
    isStale: boolean;
    conflict: AgentConflict | null;
  };
  const options = useMemo<Option[]>(() => {
    const conflictFor = (identityHex: string) =>
      agentConflict({ identityHex, site, bindings, registered, sites });
    const liveHexes = new Set(available.map((a) => a.identityHex));
    const out: Option[] = available.map((a) => ({
      value: a.identityHex,
      label: a.displayName,
      isConnected: a.isConnected,
      isStale: false,
      conflict: conflictFor(a.identityHex),
    }));
    if (current && !liveHexes.has(currentHex)) {
      out.unshift({
        value: currentHex,
        label: current.displayName,
        isConnected: false,
        isStale: true,
        conflict: conflictFor(currentHex),
      });
    }
    return out;
  }, [available, current, currentHex, site, bindings, registered, sites]);

  const currentConflict = current
    ? (options.find((o) => o.value === currentHex)?.conflict ?? null)
    : null;

  const bindingLines = useMemo<AgentBindingLine[]>(
    () =>
      sites
        .map((s) => {
          const b = bindings.find((x) => x.projectId === s.projectName);
          const c = b
            ? agentConflict({ identityHex: b.identityHex, site: s, bindings, registered, sites })
            : null;
          return {
            key: s.projectName,
            siteName: s.name,
            agentName: siteAgentText(b, hasSiteMembership(mySites, s.projectName)),
            problem: c ? conflictLabel(c) : null,
            isCurrentSite: s.projectName === projectId,
          };
        }),
    [sites, bindings, registered, projectId, mySites],
  );

  const selected = currentHex;

  async function handleChange(value: string) {
    if (value === '') return;
    // Resolve the picked option back to its Identity object. Try the
    // live list first; fall back to the configured row when the
    // pinned "(offline)" current option was picked.
    const liveMatch = available.find((a) => a.identityHex === value);
    const agentIdentity =
      liveMatch?.userIdentity ??
      (current && currentHex === value ? current.agentIdentity : null);
    const displayName =
      liveMatch?.displayName ??
      (current && currentHex === value ? current.displayName : '');
    if (!agentIdentity) return;
    // Awaited: the module refuses an agent that already serves another
    // project (#219), and that refusal must replace the success toast.
    const ok = await runReducer('Set agent', async (conn) => {
      await conn.reducers.setProjectAgent({
        projectId,
        agentIdentity,
        displayName,
      });
      return true;
    });
    if (ok) {
      toast({
        title: 'Agent updated',
        sub: `${displayName} for ${siteName}`,
        kind: 'good',
      });
    }
  }

  async function handleClear() {
    if (!current) return;
    const ok = await runReducer('Clear agent', async (conn) => {
      await conn.reducers.removeProjectAgent({ projectId });
      return true;
    });
    if (ok) {
      toast({
        title: 'Agent cleared',
        sub: `${siteName} has no onboarding agent configured`,
        kind: 'warn',
      });
    }
  }

  const isReady = availableReady && projectAgentsReady;
  const isBusy = busy !== null;

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '160px 1fr auto',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Onboarding agent</div>
          <div style={{ color: 'var(--muted)', fontSize: 11.5 }}>
            Required for <b>Orientation Assignment · Assign</b>.
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <select
            className="inp"
            value={selected}
            onChange={(e) => handleChange(e.target.value)}
            disabled={isBusy || !isReady || options.length === 0}
          >
            <option value="" disabled>
              {isReady ? '— pick an agent —' : 'Loading…'}
            </option>
            {options.map((o) => (
              <option
                key={o.value}
                value={o.value}
                // Another site's agent can't be picked; the CURRENT one stays
                // selectable so the select still shows what is saved.
                disabled={o.conflict !== null && o.value !== currentHex}
              >
                {o.label}
                {o.isStale
                  ? ' · offline (current)'
                  : o.isConnected
                    ? ' · online'
                    : ' · offline'}
                {o.conflict ? ` · ${conflictLabel(o.conflict)}` : ''}
              </option>
            ))}
          </select>
          {isReady && options.length === 0 ? (
            <div style={{ color: 'var(--warn)', fontSize: 11 }}>
              No onboarding agents online. Start the mp agent service first.
            </div>
          ) : current ? (
            <div style={{ color: 'var(--muted)', fontSize: 11 }}>
              Current: <b>{current.displayName}</b>{' '}
              <code style={{ fontSize: 10 }}>{shortenHex(currentHex)}</code>
            </div>
          ) : (
            <div style={{ color: 'var(--warn)', fontSize: 11 }}>
              No agent configured — Assign will reject until one is picked.
            </div>
          )}
          {currentConflict ? (
            <div role="alert" style={{ color: 'var(--warn)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Icon of={Icons.status.warning} size={12} /> {currentBindingWarning(currentConflict, siteName)}
            </div>
          ) : null}
        </div>
        {current ? (
          <button
            type="button"
            className="btn sm ghost"
            onClick={handleClear}
            disabled={isBusy}
            title="Remove the configured agent (Assign will reject again until one is picked)"
          >
            {busy === 'Clear agent' ? 'Clearing…' : 'Clear'}
          </button>
        ) : (
          <div />
        )}
      </div>
      <AgentBindingsList lines={bindingLines} />
    </div>
  );
}

function shortenHex(hex: string): string {
  if (hex.length <= 12) return hex;
  return `${hex.slice(0, 6)}…${hex.slice(-4)}`;
}
